import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import type { FlightPlan, WorldData } from "../../../domain/types";


export function WorldMap({ world, flightPlans }: { world: WorldData; flightPlans: FlightPlan[] }) {
  const mapWidth = 1000;
  const mapHeight = 420;
  const [zoom, setZoom] = useState(1);
  const [mapCenter, setMapCenter] = useState({ x: mapWidth / 2, y: mapHeight / 2 });
  const [isPanning, setIsPanning] = useState(false);
  const panPointerRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const normalizedZoom = normalizedMapZoom(zoom);
  const width = mapWidth / normalizedZoom;
  const height = mapHeight / normalizedZoom;
  const minCenterX = width / 2;
  const maxCenterX = mapWidth - width / 2;
  const minCenterY = height / 2;
  const maxCenterY = mapHeight - height / 2;
  const viewX = clamp(mapCenter.x - width / 2, 0, mapWidth - width);
  const viewY = clamp(mapCenter.y - height / 2, 0, mapHeight - height);
  const viewBox = `${viewX} ${viewY} ${width} ${height}`;

  useEffect(() => {
    setMapCenter((value) => {
      const next = {
        x: clamp(value.x, minCenterX, maxCenterX),
        y: clamp(value.y, minCenterY, maxCenterY)
      };
      return stableMapCenter(value, next);
    });
  }, [maxCenterX, maxCenterY, minCenterX, minCenterY]);

  const beginPan = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 || normalizedZoom <= 1) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    panPointerRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    setIsPanning(true);
  };

  const panMap = (event: ReactPointerEvent<SVGSVGElement>) => {
    const previous = panPointerRef.current;
    if (!previous || previous.pointerId !== event.pointerId) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    event.preventDefault();
    const deltaX = ((event.clientX - previous.x) / rect.width) * width;
    const deltaY = ((event.clientY - previous.y) / rect.height) * height;
    panPointerRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    setMapCenter((value) => ({
      x: clamp(value.x - deltaX, minCenterX, maxCenterX),
      y: clamp(value.y - deltaY, minCenterY, maxCenterY)
    }));
  };

  const endPan = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (panPointerRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panPointerRef.current = null;
    setIsPanning(false);
  };

  const resetMap = () => {
    setZoom(1);
    setMapCenter({ x: mapWidth / 2, y: mapHeight / 2 });
  };

  const point = (code: string) => {
    const airport = world.airports.find((item) => item.code === code);
    if (!airport) return { x: 0, y: 0 };
    return {
      x: ((airport.lon + 180) / 360) * mapWidth,
      y: ((90 - airport.lat) / 180) * mapHeight
    };
  };

  return (
    <div className="world-map-frame">
      <div className="map-toolbar" aria-label="地图缩放">
        <button className="icon-button" title="缩小地图" onClick={() => setZoom((value) => Math.max(1, Number((value - 0.25).toFixed(2))))}>
          <ZoomOut size={14} />
        </button>
        <input
          className="map-zoom-slider"
          type="range"
          min={1}
          max={3}
          step={0.25}
          value={normalizedZoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          aria-label="地图缩放比例"
        />
        <button className="icon-button" title="放大地图" onClick={() => setZoom((value) => Math.min(3, Number((value + 0.25).toFixed(2))))}>
          <ZoomIn size={14} />
        </button>
        <button className="icon-button" title="重置缩放" onClick={resetMap}>
          <Maximize2 size={14} />
        </button>
        <span>{Math.round(normalizedZoom * 100)}%</span>
      </div>
      <svg
        className={worldMapClassName(normalizedZoom > 1, isPanning)}
        viewBox={viewBox}
        role="img"
        aria-label="GM 计划网络图"
        onPointerDown={beginPan}
        onPointerMove={panMap}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onLostPointerCapture={() => {
          panPointerRef.current = null;
          setIsPanning(false);
        }}
      >
        <defs>
          <pattern id="grid" width="36" height="24" patternUnits="userSpaceOnUse">
            <path d="M 36 0 L 0 0 0 24" fill="none" stroke="rgba(165, 198, 210, .18)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="1000" height="420" fill="#101923" />
        <rect width="1000" height="420" fill="url(#grid)" />
        <path d="M80 130 C180 70 280 120 355 90 C460 55 525 105 610 88 C720 66 830 92 940 54" className="coast-line" />
        <path d="M90 260 C210 230 310 285 430 240 C520 205 650 268 770 230 C850 206 900 245 960 220" className="coast-line weak" />
        {flightPlans.map((plan) => {
          const a = point(plan.origin);
          const b = point(plan.destination);
          return <line key={plan.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="route-line" />;
        })}
        {world.airports.map((airport) => {
          const p = point(airport.code);
          return (
            <g key={airport.code} className="airport-dot">
              <circle cx={p.x} cy={p.y} r={airportDotRadius(airport.slotCapacity)} />
              <text x={p.x + 7} y={p.y + 3}>
                {airport.code}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function clamp(value: number, min: number, max: number) {
  const protectedValue = Math.max(min, value);
  return Math.min(max, protectedValue);
}

function normalizedMapZoom(zoom: number): number {
  const cappedZoom = Math.min(3, zoom);
  return Math.max(1, cappedZoom);
}

function stableMapCenter(current: { x: number; y: number }, next: { x: number; y: number }): { x: number; y: number } {
  if (next.x === current.x && next.y === current.y) {
    return current;
  }
  return next;
}

function airportDotRadius(slotCapacity: number): number {
  if (slotCapacity > 70) {
    return 5.5;
  }
  return 4;
}

function worldMapClassName(canPan: boolean, isPanning: boolean): string {
  const classes = ["world-map"];
  if (canPan) {
    classes.push("can-pan");
  }
  if (isPanning) {
    classes.push("is-panning");
  }
  return classes.join(" ");
}
